// src/pages/Admin/Business/Finance/FinanceCommitmentsPage.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaEuroSign,
  FaExclamationTriangle,
  FaFilter,
  FaLayerGroup,
  FaPlus,
  FaReceipt,
  FaSearch,
  FaSyncAlt,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../../hooks/useScrollToTop";

import {
  getAllDepensesPro,
  deleteDepensePro,
} from "../../../../../services/depensesproServices";
import {
  getAllEcheances,
  deleteEcheance,
} from "../../../../../services/echeanceServices";

import CommitmentsTable from "./components/CommitmentsTable";
import CommitmentsTimeline from "./components/CommitmentsTimeline";
import CommitmentsStatusDonut from "./charts/CommitmentsStatusDonut";
import UpcomingCommitmentsBar from "./charts/UpcomingCommitmentsBar";
import ExpenseModal from "./modals/ExpenseModal";
import DeadlineModal from "./modals/DeadlineModal";

import {
  addDays,
  buildCommitments,
  buildStatusRows,
  buildUpcomingRows,
  fmtMoney,
  formatDate,
  getCommitmentMeta,
  parseISODate,
  safeNumber,
  sortCommitments,
  startOfDay,
} from "./financeCommitments.utils";

const CARD =
  "rounded-[26px] border border-slate-200/80 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 shadow-[0_18px_50px_-20px_rgba(15,23,42,0.28)] backdrop-blur";

const DARK_GLASS =
  "rounded-[22px] border border-white/15 bg-white/10 backdrop-blur-md shadow-[0_12px_35px_-18px_rgba(0,0,0,0.45)]";

const INPUT =
  "w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/40";

const BUTTON_SOFT =
  "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium transition";

const HERO_BG =
  "relative overflow-hidden rounded-[30px] bg-slate-950 text-white border border-slate-800 shadow-[0_25px_70px_-25px_rgba(15,23,42,0.85)]";

const WINDOW_OPTIONS = [
  { value: "all", label: "Toutes les dates" },
  { value: "7", label: "Sous 7 jours" },
  { value: "30", label: "Sous 30 jours" },
  { value: "60", label: "Sous 60 jours" },
  { value: "90", label: "Sous 90 jours" },
  { value: "late", label: "En retard / passées" },
];

function StatCard({ icon, label, value, hint, tone = "light" }) {
  return (
    <div
      className={
        tone === "dark"
          ? `${DARK_GLASS} p-4`
          : "rounded-[22px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm"
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className={`text-xs uppercase tracking-[0.18em] ${
              tone === "dark"
                ? "text-white/60"
                : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {label}
          </div>
          <div
            className={`mt-2 text-2xl font-semibold tabular-nums ${
              tone === "dark"
                ? "text-white"
                : "text-slate-900 dark:text-slate-50"
            }`}
          >
            {value}
          </div>
          {hint ? (
            <div
              className={`mt-1 text-xs ${
                tone === "dark"
                  ? "text-white/70"
                  : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {hint}
            </div>
          ) : null}
        </div>

        <div
          className={`shrink-0 rounded-2xl p-3 ${
            tone === "dark"
              ? "bg-white/10 text-white"
              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          }`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, description, action }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow ? (
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500">
            {eyebrow}
          </div>
        ) : null}
        <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
          {title}
        </h2>
        {description ? (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div>{action}</div> : null}
    </div>
  );
}

function FilterChip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
        active
          ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
          : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    green:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    red: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    yellow:
      "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    orange:
      "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
    purple:
      "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone] || tones.gray}`}
    >
      {children}
    </span>
  );
}

function EmptyBlock({ text }) {
  return (
    <div className="grid h-[240px] place-items-center text-sm text-slate-400 dark:text-slate-500">
      {text}
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="p-5 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse"
        />
      ))}
    </div>
  );
}

const FinanceCommitmentsPage = () => {
  useScrollToTop();

  const [expenses, setExpenses] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAssociation, setSelectedAssociation] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedWindow, setSelectedWindow] = useState("all");
  const [selectedRenewal, setSelectedRenewal] = useState("all");
  const [sortBy, setSortBy] = useState("due_asc");

  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [deadlineModalOpen, setDeadlineModalOpen] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState(null);
  const [deadlineToEdit, setDeadlineToEdit] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [expensesData, deadlinesData] = await Promise.all([
        getAllDepensesPro(),
        getAllEcheances(),
      ]);

      setExpenses(Array.isArray(expensesData) ? expensesData : []);
      setDeadlines(Array.isArray(deadlinesData) ? deadlinesData : []);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement des dépenses et échéances.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const commitments = useMemo(
    () => buildCommitments(expenses, deadlines),
    [expenses, deadlines],
  );

  const associationOptions = useMemo(() => {
    const map = new Map();
    commitments.forEach((item) => {
      if (
        item.associationId &&
        item.associationName &&
        !map.has(item.associationId)
      ) {
        map.set(item.associationId, item.associationName);
      }
    });

    return Array.from(map.entries())
      .map(([id, nom]) => ({ id, nom }))
      .sort((a, b) => a.nom.localeCompare(b.nom, "fr"));
  }, [commitments]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    commitments.forEach((item) => {
      if (item.category) set.add(item.category);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [commitments]);

  const matchesWindow = useCallback((item, windowValue) => {
    if (windowValue === "all") return true;

    const meta = getCommitmentMeta(item);

    if (meta.days === null) {
      return windowValue === "nodate";
    }

    if (windowValue === "late") {
      return meta.days < 0;
    }

    const max = Number(windowValue);

    if (!Number.isFinite(max)) return true;

    return meta.days >= 0 && meta.days <= max;
  }, []);

  const filteredCommitments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return commitments.filter((item) => {
      const haystack = [
        item.title,
        item.category,
        item.counterparty,
        item.associationName,
        item.originLabel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = !term || haystack.includes(term);

      const matchesAssociation = selectedAssociation
        ? String(item.associationId) === String(selectedAssociation)
        : true;

      const matchesCategory = selectedCategory
        ? item.category === selectedCategory
        : true;

      const matchesType =
        selectedType === "all" ? true : item.entityType === selectedType;

      const matchesRenewal =
        selectedRenewal === "all"
          ? true
          : selectedRenewal === "auto"
            ? item.entityType === "echeance" && item.autoRenew
            : item.entityType === "echeance" && !item.autoRenew;

      const matchesDateWindow = matchesWindow(item, selectedWindow);

      return (
        matchesSearch &&
        matchesAssociation &&
        matchesCategory &&
        matchesType &&
        matchesRenewal &&
        matchesDateWindow
      );
    });
  }, [
    commitments,
    searchTerm,
    selectedAssociation,
    selectedCategory,
    selectedType,
    selectedRenewal,
    selectedWindow,
    matchesWindow,
  ]);

  const displayedCommitments = useMemo(() => {
    return [...filteredCommitments].sort((a, b) =>
      sortCommitments(a, b, sortBy),
    );
  }, [filteredCommitments, sortBy]);

  const upcomingRows = useMemo(
    () => buildUpcomingRows(displayedCommitments, 6),
    [displayedCommitments],
  );

  const statusRows = useMemo(
    () => buildStatusRows(displayedCommitments),
    [displayedCommitments],
  );

  const dashboard = useMemo(() => {
    const today = startOfDay(new Date());
    const next30 = addDays(today, 30);

    let upcoming30Count = 0;
    let upcoming30Amount = 0;
    let overdueCount = 0;
    let autoRenewCount = 0;
    let expenseMonthAmount = 0;
    let estimatedFutureAmount = 0;

    displayedCommitments.forEach((item) => {
      const meta = getCommitmentMeta(item);

      if (meta.days !== null && meta.days >= 0 && meta.days <= 30) {
        upcoming30Count += 1;
        upcoming30Amount += safeNumber(item.displayAmount);
      }

      if (meta.days !== null && meta.days < 0) {
        overdueCount += 1;
      }

      if (item.entityType === "echeance" && item.autoRenew) {
        autoRenewCount += 1;
      }

      if (item.entityType === "depense") {
        const expenseDate = parseISODate(item.dueDate);
        if (
          expenseDate &&
          expenseDate.getMonth() === today.getMonth() &&
          expenseDate.getFullYear() === today.getFullYear()
        ) {
          expenseMonthAmount += safeNumber(item.amount);
        }
      }

      const due = parseISODate(
        item.entityType === "echeance"
          ? item.endDate || item.startDate
          : item.dueDate,
      );

      if (due && due >= today && due <= next30) {
        estimatedFutureAmount += safeNumber(item.displayAmount);
      }
    });

    return {
      total: displayedCommitments.length,
      upcoming30Count,
      upcoming30Amount,
      overdueCount,
      autoRenewCount,
      expenseMonthAmount,
      estimatedFutureAmount,
    };
  }, [displayedCommitments]);

  const upcomingList = useMemo(() => {
    return displayedCommitments
      .filter((item) => {
        const meta = getCommitmentMeta(item);
        return meta.days !== null && meta.days >= 0;
      })
      .sort((a, b) => sortCommitments(a, b, "due_asc"))
      .slice(0, 8);
  }, [displayedCommitments]);

  const activeFilters = [
    selectedAssociation
      ? associationOptions.find(
          (a) => String(a.id) === String(selectedAssociation),
        )?.nom
      : null,
    selectedCategory || null,
    selectedType === "depense"
      ? "Dépenses"
      : selectedType === "echeance"
        ? "Échéances"
        : null,
    selectedRenewal === "auto"
      ? "Renouvellement auto"
      : selectedRenewal === "manual"
        ? "Sans auto-renouvellement"
        : null,
    selectedWindow !== "all"
      ? WINDOW_OPTIONS.find((w) => w.value === selectedWindow)?.label
      : null,
  ].filter(Boolean);

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedAssociation("");
    setSelectedCategory("");
    setSelectedType("all");
    setSelectedWindow("all");
    setSelectedRenewal("all");
    setSortBy("due_asc");
  };

  const handleDelete = async (item) => {
    const label =
      item.entityType === "depense" ? "cette dépense" : "cette échéance";

    if (!window.confirm(`Supprimer ${label} ?`)) return;

    try {
      if (item.entityType === "depense") {
        await deleteDepensePro(item.id);
      } else {
        await deleteEcheance(item.id);
      }

      toast.success("Élément supprimé avec succès.");
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const openCreateExpense = () => {
    setExpenseToEdit(null);
    setExpenseModalOpen(true);
  };

  const openCreateDeadline = () => {
    setDeadlineToEdit(null);
    setDeadlineModalOpen(true);
  };

  const openEditExpense = (expense) => {
    setExpenseToEdit(expense);
    setExpenseModalOpen(true);
  };

  const openEditDeadline = (deadline) => {
    setDeadlineToEdit(deadline);
    setDeadlineModalOpen(true);
  };

  const isEmpty = !loading && displayedCommitments.length === 0;

  return (
    <div className="space-y-6">
      <section className={HERO_BG}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(129,140,248,0.35),transparent_30%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.30),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(34,197,94,0.22),transparent_30%)]" />
        <div className="relative p-6 md:p-8">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                <FaCalendarAlt />
                Pilotage financier
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                Dépenses pro & échéances à venir
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/75 md:text-base">
                Une vue unique pour suivre les paiements déjà passés, les
                échéances futures, les renouvellements automatiques et le
                montant estimé des prochaines sorties d’argent.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={openCreateExpense}
                className={`${BUTTON_SOFT} bg-white text-slate-900 hover:bg-slate-100`}
              >
                <FaPlus />
                Ajouter une dépense
              </button>

              <button
                type="button"
                onClick={openCreateDeadline}
                className={`${BUTTON_SOFT} bg-white/10 text-white hover:bg-white/20 border border-white/15`}
              >
                <FaPlus />
                Ajouter une échéance
              </button>

              <button
                type="button"
                onClick={fetchData}
                className={`${BUTTON_SOFT} bg-white/10 text-white hover:bg-white/20 border border-white/15`}
              >
                <FaSyncAlt />
                Rafraîchir
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
            <StatCard
              tone="dark"
              icon={<FaLayerGroup />}
              label="Engagements affichés"
              value={dashboard.total}
              hint="Dépenses + échéances"
            />
            <StatCard
              tone="dark"
              icon={<FaClock />}
              label="Sous 30 jours"
              value={dashboard.upcoming30Count}
              hint="Éléments à surveiller"
            />
            <StatCard
              tone="dark"
              icon={<FaEuroSign />}
              label="Montant estimé 30j"
              value={fmtMoney(dashboard.upcoming30Amount)}
              hint="Dépenses + estimations d’échéances"
            />
            <StatCard
              tone="dark"
              icon={<FaExclamationTriangle />}
              label="En retard / passés"
              value={dashboard.overdueCount}
              hint="À traiter ou à archiver"
            />
            <StatCard
              tone="dark"
              icon={<FaCheckCircle />}
              label="Auto-renouvellements"
              value={dashboard.autoRenewCount}
              hint="Échéances reconduites automatiquement"
            />
            <StatCard
              tone="dark"
              icon={<FaReceipt />}
              label="Dépenses du mois"
              value={fmtMoney(dashboard.expenseMonthAmount)}
              hint="Montants réellement saisis"
            />
          </div>
        </div>
      </section>

      <section className={`${CARD} p-5 md:p-6`}>
        <SectionHead
          eyebrow="Filtres"
          title="Trouver vite la bonne dépense ou la bonne échéance"
          description="Recherche, filtres par association, catégorie, type, fenêtre temporelle et renouvellement."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className={`${BUTTON_SOFT} bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700`}
            >
              Réinitialiser
            </button>
          }
        />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
          <div className="relative xl:col-span-2">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={`${INPUT} pl-11`}
              type="text"
              placeholder="Rechercher : nom, catégorie, destinataire, association…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className={INPUT}
            value={selectedAssociation}
            onChange={(e) => setSelectedAssociation(e.target.value)}
          >
            <option value="">Toutes les associations</option>
            {associationOptions.map((asso) => (
              <option key={asso.id} value={asso.id}>
                {asso.nom}
              </option>
            ))}
          </select>

          <select
            className={INPUT}
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Toutes les catégories</option>
            {categoryOptions.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          <select
            className={INPUT}
            value={selectedWindow}
            onChange={(e) => setSelectedWindow(e.target.value)}
          >
            {WINDOW_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>

          <select
            className={INPUT}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="due_asc">Tri : date la plus proche</option>
            <option value="due_desc">Tri : date la plus lointaine</option>
            <option value="amount_desc">Tri : montant décroissant</option>
            <option value="amount_asc">Tri : montant croissant</option>
            <option value="association_asc">Tri : association A → Z</option>
            <option value="title_asc">Tri : libellé A → Z</option>
          </select>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
          <select
            className={INPUT}
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="all">Tous les types</option>
            <option value="depense">Dépenses</option>
            <option value="echeance">Échéances</option>
          </select>

          <select
            className={INPUT}
            value={selectedRenewal}
            onChange={(e) => setSelectedRenewal(e.target.value)}
          >
            <option value="all">Tous les renouvellements</option>
            <option value="auto">Auto-renouvellement</option>
            <option value="manual">Sans auto-renouvellement</option>
          </select>

          <div className="flex items-center gap-2 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
            <FaFilter />
            <span>
              {displayedCommitments.length} résultat
              {displayedCommitments.length > 1 ? "s" : ""}
            </span>
            {activeFilters.length ? (
              <span className="hidden md:inline">
                • {activeFilters.join(" • ")}
              </span>
            ) : (
              <span className="hidden md:inline">
                • aucun filtre avancé actif
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <FilterChip
            active={selectedType === "all"}
            onClick={() => setSelectedType("all")}
          >
            Tout
          </FilterChip>
          <FilterChip
            active={selectedType === "depense"}
            onClick={() => setSelectedType("depense")}
          >
            Dépenses
          </FilterChip>
          <FilterChip
            active={selectedType === "echeance"}
            onClick={() => setSelectedType("echeance")}
          >
            Échéances
          </FilterChip>
          <FilterChip
            active={selectedWindow === "30"}
            onClick={() => setSelectedWindow("30")}
          >
            Sous 30 jours
          </FilterChip>
          <FilterChip
            active={selectedWindow === "late"}
            onClick={() => setSelectedWindow("late")}
          >
            En retard
          </FilterChip>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <section className={`${CARD} p-5 md:p-6 xl:col-span-5`}>
          <SectionHead
            eyebrow="Focus"
            title="Prochains paiements / renouvellements"
            description="Les éléments les plus proches à surveiller en priorité."
          />

          {upcomingList.length === 0 ? (
            <EmptyBlock text="Aucun engagement futur avec les filtres actuels." />
          ) : (
            <div className="space-y-3">
              {upcomingList.map((item) => {
                const meta = getCommitmentMeta(item);

                return (
                  <div
                    key={item.uid}
                    className="flex flex-col gap-3 rounded-[22px] border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/40 p-4"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-semibold text-slate-900 dark:text-slate-50">
                        {item.title}
                      </div>
                      <Badge
                        tone={item.entityType === "depense" ? "blue" : "purple"}
                      >
                        {item.entityType === "depense" ? "Dépense" : "Échéance"}
                      </Badge>
                      <Badge tone={meta.tone}>{meta.label}</Badge>
                    </div>

                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {item.associationName} • {item.category}
                    </div>

                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                      <MiniInfo
                        label="Date"
                        value={formatDate(item.dueDate || item.endDate)}
                      />
                      <MiniInfo
                        label="Montant"
                        value={fmtMoney(item.displayAmount, true)}
                      />
                      <MiniInfo
                        label="Tiers"
                        value={item.counterparty || "—"}
                      />
                      <MiniInfo
                        label="Auto"
                        value={
                          item.entityType === "echeance"
                            ? item.autoRenew
                              ? "Oui"
                              : "Non"
                            : "—"
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className={`${CARD} p-5 md:p-6 xl:col-span-7`}>
          <SectionHead
            eyebrow="Timeline"
            title="Vue planning des engagements"
            description="Lecture rapide façon mini diagramme de Gantt sur les 120 prochains jours."
          />

          <CommitmentsTimeline items={displayedCommitments} daysWindow={120} />
        </section>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <section className={`${CARD} p-5 md:p-6 xl:col-span-5`}>
          <SectionHead
            eyebrow="Alertes"
            title="Répartition des urgences"
            description="Vision rapide des éléments urgents, proches, lointains ou en retard."
          />
          <CommitmentsStatusDonut data={statusRows} />
        </section>

        <section className={`${CARD} p-5 md:p-6 xl:col-span-7`}>
          <SectionHead
            eyebrow="Projection"
            title="Échéances par mois"
            description="Nombre d’éléments et montant estimé sur les 6 prochains mois."
          />
          <UpcomingCommitmentsBar data={upcomingRows} />
        </section>
      </div>

      <section className={`${CARD} overflow-hidden`}>
        <div className="border-b border-slate-200 dark:border-slate-800 px-5 py-4 md:px-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Liste complète des engagements
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Une seule table pour les dépenses et les échéances, avec tri,
                alertes, montant estimé et actions.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="gray">
                {displayedCommitments.length} résultat
                {displayedCommitments.length > 1 ? "s" : ""}
              </Badge>
              {selectedType !== "all" ? (
                <Badge tone={selectedType === "depense" ? "blue" : "purple"}>
                  {selectedType === "depense" ? "Dépenses" : "Échéances"}
                </Badge>
              ) : null}
              {selectedWindow !== "all" ? (
                <Badge tone="orange">
                  {
                    WINDOW_OPTIONS.find((item) => item.value === selectedWindow)
                      ?.label
                  }
                </Badge>
              ) : null}
            </div>
          </div>
        </div>

        {loading ? (
          <SkeletonTable />
        ) : isEmpty ? (
          <div className="p-10 text-center">
            <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl dark:bg-slate-800">
              📭
            </div>
            <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">
              Aucun élément ne correspond aux filtres
            </div>
            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Élargis la recherche ou réinitialise les filtres.
            </div>
          </div>
        ) : (
          <CommitmentsTable
            items={displayedCommitments}
            onEditExpense={openEditExpense}
            onEditDeadline={openEditDeadline}
            onDelete={handleDelete}
          />
        )}
      </section>

      <ExpenseModal
        isOpen={expenseModalOpen}
        onClose={() => {
          setExpenseModalOpen(false);
          setExpenseToEdit(null);
        }}
        expense={expenseToEdit}
        refreshList={fetchData}
      />

      <DeadlineModal
        isOpen={deadlineModalOpen}
        onClose={() => {
          setDeadlineModalOpen(false);
          setDeadlineToEdit(null);
        }}
        deadline={deadlineToEdit}
        refreshList={fetchData}
      />

      <ToastContainer />
    </div>
  );
};

function MiniInfo({ label, value }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 p-3">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 font-medium text-slate-900 dark:text-slate-50">
        {value}
      </div>
    </div>
  );
}

export default FinanceCommitmentsPage;
